import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useMediaQuery, useSeasonArt } from '../context';

/**
 * The home page's backdrop: moonlit sky with the town's rooftops and bare
 * trees along the bottom of the window, a fog band at their feet and the moon
 * top right. It is fixed to the viewport (portaled to <body>, below the
 * ambient layer and all content), so the page scrolls over a scene that stays
 * put. A shade fades in as the visitor scrolls past the hero, so cards and
 * text further down sit on a darker sky; only its opacity changes. Until the
 * raster scene exists the hero keeps the house light re-lit as moonlight
 * (halloween.css) and just the moon shows here.
 */
export function HalloweenHeroScene() {
  const notPhone = useMediaQuery('(min-width: 768px)');
  const scene = useSeasonArt('hero-scene');
  const fog = useSeasonArt('hero-fog', notPhone);
  const moon = useSeasonArt('moon', notPhone);
  const shade = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = shade.current;
    if (!el) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      // Fully shaded one and a half screens down.
      const t = Math.min(window.scrollY / (window.innerHeight * 1.5), 1);
      el.style.opacity = String(t);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return createPortal(
    <div className="hw-hero-scene" aria-hidden="true">
      {scene && <div className="hw-hero-sky" style={{ backgroundImage: `url("${scene}")` }} />}
      <div className={`hw-moon${moon ? ' is-raster' : ''}`} style={moon ? { backgroundImage: `url("${moon}")` } : undefined} />
      {fog && (
        <div className="hw-hero-fog">
          <div className="hw-hero-fog-band" style={{ backgroundImage: `url("${fog}")` }} />
        </div>
      )}
      <div ref={shade} className="hw-scene-shade" />
    </div>,
    document.body,
  );
}

/** The 404 illustration: a door left ajar on an empty house. Text-only without it. */
export function HalloweenNotFoundArt() {
  const art = useSeasonArt('not-found');
  if (!art) return null;
  return (
    <img
      src={art}
      alt=""
      aria-hidden="true"
      width={480}
      height={320}
      className="hw-not-found-art mx-auto mb-2 block h-auto w-full max-w-[480px]"
    />
  );
}
